import * as React from "react"
import { formatPrice } from "@/lib/format-utils"
import type { CollectionCategoryStats } from "@/lib/collection-category-stats"

/**
 * Type-vs-type comparison — FRIDGE-401 Step 4, the comparison-depth module a
 * retailer category page does not publish: every type measured side by side
 * from the live cross-retailer catalog (models tracked, median tracked
 * price, typical capacity), each row linking that type's own collection.
 *
 * Real-or-Hidden:
 * - Renders nothing without stats, and nothing below 2 comparable rows — a
 *   one-row "comparison" is not a comparison.
 * - Rows arrive pre-filtered (lib/collection-category-stats.ts: a type needs
 *   ≥2 members and a real median to earn a row) and pre-gated (a row whose
 *   collection is not live+indexed carries slug null and renders unlinked).
 * - Typical capacity cells render "—" when the type has no measured
 *   capacity; capacity renders to 2 decimals (the sitewide precision rule).
 *
 * The table scrolls inside its own container on narrow viewports — the page
 * never scrolls horizontally.
 */
export function CollectionTypeComparison({
  title,
  stats,
}: {
  title: string
  stats: CollectionCategoryStats | null
}) {
  if (!stats) return null
  const rows = stats.typeRows
  if (rows.length < 2) return null

  return (
    <section
      id="collection-type-comparison"
      aria-label={`Compare ${title.toLowerCase()} by type`}
      className="bg-white py-16"
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-8">
        <div className="mb-6">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#ff9951] mb-2">
            Type by Type
          </p>
          <h2 className="text-2xl md:text-3xl font-black tracking-tight text-gray-900">
            Compare {title} by Type
          </h2>
          <p className="mt-2 max-w-2xl text-sm md:text-base leading-relaxed text-gray-500">
            Every figure below is measured live from the tracked cross-retailer
            catalog — the count, the median of each model&apos;s lowest tracked
            price, and the typical (median) capacity per type.
          </p>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-gray-200">
          <table className="w-full min-w-[560px] text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/70">
                <th scope="col" className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  Type
                </th>
                <th scope="col" className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  Models tracked
                </th>
                <th scope="col" className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  Median tracked price
                </th>
                <th scope="col" className="px-4 py-3 text-[11px] font-bold uppercase tracking-[0.08em] text-gray-500">
                  Typical capacity
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.label} className="border-b border-gray-100 last:border-b-0">
                  <th scope="row" className="px-4 py-3 font-bold text-gray-900">
                    {row.slug ? (
                      <a
                        href={`/collections/${row.slug}`}
                        className="text-gray-900 underline decoration-gray-300 underline-offset-4 transition-colors hover:text-[#ff9951] hover:decoration-[#ff9951]"
                      >
                        {row.label}
                      </a>
                    ) : (
                      row.label
                    )}
                  </th>
                  <td className="px-4 py-3 tabular-nums text-gray-700">
                    {row.count.toLocaleString("en-US")}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-700">
                    {row.medianPrice !== null ? formatPrice(row.medianPrice) : "—"}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-700">
                    {row.medianCapacityCuft !== null
                      ? `${row.medianCapacityCuft.toFixed(2)} cu ft`
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  )
}
